<template>
  <div class="attribute-coverage-page">
    <h2>Attribute Coverage Report</h2>

    <!-- Controls Bar -->
    <div class="controls-bar">
      <!-- DB selector -->
      <div class="control-group">
        <label for="dbSelectCoverage">Database:</label>
        <select
          id="dbSelectCoverage"
          :value="selectedDb"
          @change="onDbChange"
          :disabled="isLoading"
        >
          <option value="production">Production</option>
          <option value="test">Test</option>
          <option value="staging">Staging</option>
        </select>
      </div>

      <!-- Only required? -->
      <div class="control-group">
        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="onlyRequired"
            @change="onOnlyRequiredToggled"
            :disabled="isLoading"
          />
          Show Required Attributes Only
        </label>
      </div>

      <!-- Hide zero coverage? -->
      <div class="control-group">
        <label class="checkbox-label">
          <input
            type="checkbox"
            :checked="hideZeroCoverage"
            @change="onHideZeroToggled"
            :disabled="isLoading"
          />
          Hide Zero Coverage
        </label>
      </div>
      <div class="control-group">
        <label class="checkbox-label">
          <input
            type="checkbox"
            v-model="removeMissingImages"
            :disabled="isLoading"
            @change="refreshData"
          />
          Ignore Products With No Images
        </label>
      </div>
      <!-- Refresh Button -->
      <div class="control-group">
        <button
          class="button"
          @click="refreshData"
          :disabled="isLoading"
        >
          Refresh Data
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-indicator">
      <div class="spinner"></div>
      Loading coverage data...
    </div>

    <!-- Error from server or local -->
    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <!-- Metadata Summary -->
    <div
      v-if="!isLoading && coverageMetadata && Object.keys(coverageMetadata).length > 0"
      class="metadata-summary"
    >
      <p>
        <strong>DB:</strong> {{ coverageMetadata.productDb }}
        (Collection: {{ coverageMetadata.productCollection }})
        <span v-if="coverageMetadata.taxonomyDb">
          |
          <strong>Taxonomies from:</strong> {{ coverageMetadata.taxonomyDb }}
          (Version: {{ coverageMetadata.taxonomyVersion }})
        </span>
      </p>
      <p>
        Found <strong>{{ coverageMetadata.categoriesFromProducts }}</strong>
        categories and <strong>{{ coverageMetadata.attributesInTaxonomy }}</strong>
        relevant attributes.
      </p>
      <p>
        Analyzed <strong>{{ coverageMetadata.totalCoverageEntries }}</strong>
        (category x attribute) pairs.
      </p>
      <p v-if="coverageMetadata.error" class="error-message">
        <strong>Error from server:</strong> {{ coverageMetadata.error }}
      </p>
    </div>

    <!-- If no coverage data -->
    <div
      v-if="!isLoading && !errorMessage && filteredCoverage.length === 0"
      class="no-data"
    >
      No coverage data found.
    </div>

    <!-- Coverage Table (Nested) -->
    <div v-if="filteredCoverage.length > 0" class="coverage-table-container">
      <table class="coverage-table">
        <thead>
          <tr>
            <th style="width: 40px;"></th>
            <th
              @click="sortBy('categoryPath')"
              :class="{ sorted: sortKey === 'categoryPath' }"
            >
              Category Path
            </th>
            <th
              @click="sortBy('docCount')"
              :class="{ sorted: sortKey === 'docCount' }"
            >
              Total Products
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Single v-for over categories -->
          <template v-for="(cat, index) in sortedCoverageCategories" :key="cat.categoryPath">
            <tr>
              <td class="expand-cell">
                <button class="expand-btn" @click="toggleExpanded(cat.categoryPath)">
                  <span v-if="isExpanded(cat.categoryPath)">-</span>
                  <span v-else>+</span>
                </button>
              </td>
              <td>{{ cat.categoryPath }}</td>
              <td class="number-cell">{{ cat.docCount }}</td>
            </tr>

            <!-- Sub-row for attributes, only if expanded -->
            <tr v-if="isExpanded(cat.categoryPath)">
              <td></td>
              <td colspan="2">
                <table class="nested-attr-table">
                  <thead>
                    <tr>
                      <th
                        @click="sortByAttr('attrId')"
                        :class="{ sorted: attrSortKey === 'attrId' }"
                      >
                        Attr ID
                      </th>
                      <th
                        @click="sortByAttr('isRequired')"
                        :class="{ sorted: attrSortKey === 'isRequired' }"
                      >
                        Required?
                      </th>
                      <th
                        @click="sortByAttr('count')"
                        :class="{ sorted: attrSortKey === 'count' }"
                      >
                        Products w/ Attr
                      </th>
                      <th
                        @click="sortByAttr('coveragePct')"
                        :class="{ sorted: attrSortKey === 'coveragePct' }"
                      >
                        Coverage %
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="attrRow in getSortedAttributes(cat)"
                      :key="attrRow.attrId"
                    >
                      <td>
                        {{ attrRow.attributeName.en }}
                        <span v-if="attrRow.attributeName" class="attr-id">
                          ({{ attrRow.attrId }})
                        </span>
                      </td>
                      <td :class="{ 'required-yes': attrRow.isRequired }">
                        {{ attrRow.isRequired ? 'Yes' : 'No' }}
                      </td>
                      <td class="number-cell">{{ attrRow.count }}</td>
                      <td class="number-cell coverage-cell">
                        <div class="coverage-bar-container">
                          <div
                            class="coverage-bar"
                            :style="{ width: (attrRow.coveragePct || 0) + '%' }"
                          ></div>
                        </div>
                        {{ (attrRow.coveragePct || 0).toFixed(1) }}%
                      </td>

                      <!-- NEW: Action buttons -->
                      <td class="coverage-actions">
                        <button
                          class="view-products-btn with-coverage"
                          @click="goViewProducts(cat.categoryPath, attrRow.attrId, true)"
                        >
                          With
                        </button>
                        <button
                          class="view-products-btn missing-coverage"
                          @click="goViewProducts(cat.categoryPath, attrRow.attrId, false)"
                        >
                          Missing
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useProductStore } from '@/store/productStore';
// 1) Import useRouter
import { useRouter } from 'vue-router';

const store = useProductStore();

// 2) Get a router instance from useRouter()
const router = useRouter();

// Local state
const errorMessage = ref('');
const onlyRequired = ref(false);
const hideZeroCoverage = ref(false);
const removeMissingImages = ref(true);

const selectedDb = ref(store.state.selectedDatabase || 'test');

// Sorting - categories
const sortKey = ref('categoryPath');
const sortAsc = ref(true);

// Sorting - attributes
const attrSortKey = ref('coveragePct');
const attrSortAsc = ref(false);

// Expand/collapse
const expandedCategories = ref(new Set());

// Computed from store
const isLoading = computed(() => store.getIsCoverageLoading());
const coverageData = computed(() => store.getCoverageData() || []);
const coverageMetadata = computed(() => store.getCoverageMetadata() || []);

// Filter attributes
function filterAttributes(cat) {
  let arr = cat.coverage || [];
  if (onlyRequired.value) {
    arr = arr.filter(a => a.isRequired);
  }
  if (hideZeroCoverage.value) {
    arr = arr.filter(a => a.count > 0);
  }
  return arr;
}

// Filter categories & embed filtered coverage
const filteredCoverage = computed(() => {
  return coverageData.value.map(cat => {
    const filtered = filterAttributes(cat);
    return { ...cat, coverage: filtered };
  });
});

// Sort categories
const sortedCoverageCategories = computed(() => {
  const arr = [...filteredCoverage.value];
  arr.sort((a, b) => {
    let x = a[sortKey.value];
    let y = b[sortKey.value];
    if (typeof x === 'string') x = x.toLowerCase();
    if (typeof y === 'string') y = y.toLowerCase();

    if (x > y) return sortAsc.value ? 1 : -1;
    if (x < y) return sortAsc.value ? -1 : 1;
    return 0;
  });
  return arr;
});

// Sort attributes
function getSortedAttributes(cat) {
  const arr = [...cat.coverage];
  arr.sort((a, b) => {
    let x = a[attrSortKey.value];
    let y = b[attrSortKey.value];

    if (typeof x === 'string') x = x.toLowerCase();
    if (typeof y === 'string') y = y.toLowerCase();

    if (x > y) return attrSortAsc.value ? 1 : -1;
    if (x < y) return attrSortAsc.value ? -1 : 1;
    return 0;
  });
  return arr;
}

// Lifecycle
onMounted(() => {
  if (!coverageData.value.length) {
    refreshData();
  }
});

// Methods
async function refreshData() {
  errorMessage.value = '';
  try {
    // Pass removeMissingImages to the store
    await store.fetchAttributeCoverage({
      dbName: selectedDb.value,
      removeMissingImages: removeMissingImages.value
    });
  } catch (err) {
    errorMessage.value = err.message || String(err);
  }
}

function onDbChange(e) {
  selectedDb.value = e.target.value;
  store.setSelectedDatabase(e.target.value);
  refreshData();
}

function onOnlyRequiredToggled(e) {
  onlyRequired.value = e.target.checked;
}

function onHideZeroToggled(e) {
  hideZeroCoverage.value = e.target.checked;
}

function sortBy(key) {
  if (sortKey.value === key) {
    sortAsc.value = !sortAsc.value;
  } else {
    sortKey.value = key;
    sortAsc.value = true;
  }
}

function sortByAttr(key) {
  if (attrSortKey.value === key) {
    attrSortAsc.value = !attrSortAsc.value;
  } else {
    attrSortKey.value = key;
    attrSortAsc.value = true;
  }
}

// 3) Use router.push where we want to navigate
function goViewProducts(categoryPath, attrId, hasAttribute) {
  // If your store expects the category path as an array, split:
  const categorySegments = categoryPath.split('|');

  store.updateCatalogFilters({
    selectedFilters: {
      // e.g. match "webCategory" or however you prefer
      alliumCat: [categoryPath]  
    },
    selectedCategoryPath: categorySegments,
    selectedAttrId: attrId,
    attrExists: hasAttribute, 
    currentPage: 1
  });

  // Now route to Catalog:
  router.push('/catalog');
}

// Expand/collapse categories
function toggleExpanded(catPath) {
  if (expandedCategories.value.has(catPath)) {
    expandedCategories.value.delete(catPath);
  } else {
    expandedCategories.value.add(catPath);
  }
}
function isExpanded(catPath) {
  return expandedCategories.value.has(catPath);
}
</script>

<style scoped>
.attribute-coverage-page {
  padding: 20px;
  font-family: sans-serif;
}
h2 {
  margin-bottom: 20px;
  color: #333;
}
.controls-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}
.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.control-group label {
  font-weight: 600;
  color: #495057;
}
.control-group select,
.control-group input[type="checkbox"] {
  padding: 6px;
  border-radius: 4px;
  border: 1px solid #ced4da;
  font-size: 14px;
  cursor: pointer;
}
.button {
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #007bff;
  background-color: #007bff;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}
.button:hover:not(:disabled) {
  background-color: #0056b3;
}
.button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}
.checkbox-label {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.loading-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #555;
  font-size: 1.1em;
  margin-top: 20px;
}
.spinner {
  width: 1.2rem;
  height: 1.2rem;
  border: 3px solid #eee;
  border-top-color: #999;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.error-message {
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 4px;
  font-weight: 500;
}
.metadata-summary {
  background-color: #e9ecef;
  padding: 10px 15px;
  margin-bottom: 20px;
  border-radius: 4px;
  font-size: 0.9em;
  color: #495057;
  border: 1px solid #ced4da;
}
.metadata-summary p {
  margin: 5px 0;
}
.coverage-table-container {
  overflow-x: auto; 
}
.coverage-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.coverage-table th,
.coverage-table td {
  border: 1px solid #dee2e6;
  padding: 10px 12px;
  text-align: left;
  font-size: 14px;
  vertical-align: middle;
}
.coverage-table th {
  background-color: #f1f3f5;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.coverage-table th.sorted::after {
  content: ' ▲';
  font-size: 0.8em;
  margin-left: 4px;
}
.coverage-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}
.coverage-table tbody tr:hover {
  background-color: #e9ecef;
}
.expand-cell {
  width: 40px;
  text-align: center;
}
.expand-btn {
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  line-height: 20px;
  text-align: center;
  font-weight: bold;
  transition: background 0.2s;
}
.expand-btn:hover {
  background: #f0f0f0;
}
.nested-attr-table {
  width: 100%;
  border-collapse: collapse;
}
.nested-attr-table th,
.nested-attr-table td {
  border: 1px solid #dee2e6;
  padding: 8px 10px;
  font-size: 13px;
}
.attr-id {
  font-size: 0.8em;
  color: #6c757d;
  margin-left: 5px;
  white-space: nowrap;
}
.number-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.required-yes {
  font-weight: 600;
  color: #28a745;
}
.coverage-cell {
  min-width: 160px;
}
.coverage-bar-container {
  display: inline-block;
  width: 80px;
  height: 12px;
  background-color: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  margin-right: 8px;
  vertical-align: middle;
}
.coverage-bar {
  height: 100%;
  background-color: #28a745; 
  border-radius: 6px 0 0 6px;
  transition: width 0.3s;
}
.coverage-bar[style*="width: 0%"],
.coverage-bar[style*="width: 1"],
.coverage-bar[style*="width: 2"],
.coverage-bar[style*="width: 3"],
.coverage-bar[style*="width: 4"] {
  background-color: #dc3545;
}
.coverage-bar[style*="width: 5"],
.coverage-bar[style*="width: 6"],
.coverage-bar[style*="width: 7"] {
  background-color: #ffc107;
}
.no-data {
  text-align: center;
  margin-top: 40px;
  color: #6c757d;
  font-size: 1.1em;
}
.coverage-actions {
  white-space: nowrap;
  text-align: center;
}
.view-products-btn {
  margin: 0 4px;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.with-coverage {
  background-color: #28a745;
  color: #fff;
}
.missing-coverage {
  background-color: #dc3545;
  color: #fff;
}
@media (max-width: 768px) {
  .controls-bar {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
